import { useCallback, useEffect, useRef, useState } from "react";
import { servicePostLivechatMessage } from "../../../services/livechat";
import { newClientId } from "./livechatState";
import { kindOf, prepareLivechatUpload, uploadLivechatMedia } from "./livechatUpload";

const NO_ACTIVE_TICKET = 409;
const localId = (clientId) => `local-${clientId}`;

// Sends one message at a time, in order. A failed message keeps its client_id, so a resend
// is answered with the stored message instead of a second one.
export const useLivechatSender = ({ token, conversationId, lastTicketId, dispatch }) => {
  const queueRef = useRef(Promise.resolve());
  const draftsRef = useRef(new Map());
  const conversationRef = useRef(conversationId);
  const [isTicketClosed, setIsTicketClosed] = useState(false);

  useEffect(() => {
    conversationRef.current = conversationId;
    draftsRef.current = new Map();
    setIsTicketClosed(false);
  }, [conversationId]);

  const deliver = useCallback(
    (clientId) => {
      const draft = draftsRef.current.get(clientId);

      if (!draft) return;

      queueRef.current = queueRef.current
        .then(async () => {
          const res = await servicePostLivechatMessage(token, draft.conversationId, {
            clientId,
            text: draft.text,
            replyToId: draft.replyTo?.id,
            attachmentId: draft.attachmentId,
            type: draft.type,
          });

          // the agent may have opened another conversation meanwhile
          if (conversationRef.current !== draft.conversationId) return;

          if (res?.status === 200 || res?.status === 201) {
            draftsRef.current.delete(clientId);
            dispatch({ type: "messages/upsert", message: res.data });
            return;
          }

          // a closed ticket: never resend this reply into the next ticket
          if (res?.status === NO_ACTIVE_TICKET) {
            draftsRef.current.delete(clientId);
            setIsTicketClosed(true);
            dispatch({ type: "messages/localStatus", id: localId(clientId), local: "rejected" });
            return;
          }

          dispatch({ type: "messages/localStatus", id: localId(clientId), local: "failed" });
        })
        // one unexpected error must not stop every later send
        .catch(() => dispatch({ type: "messages/localStatus", id: localId(clientId), local: "failed" }));
    },
    [token, dispatch],
  );

  const send = useCallback(
    (text, replyTo) => {
      const clientId = newClientId();
      draftsRef.current.set(clientId, { conversationId: conversationRef.current, text, replyTo });
      dispatch({
        type: "messages/upsert",
        message: {
          id: localId(clientId),
          client_id: clientId,
          type: "text",
          text,
          reply_to: replyTo ? { id: replyTo.id, type: replyTo.type, text: replyTo.text, deleted: false } : undefined,
          sender: { type: "agent" },
          ticket_id: lastTicketId,
          created_at: new Date().toISOString(),
          local: "sending",
        },
      });
      deliver(clientId);
    },
    [dispatch, deliver, lastTicketId],
  );

  // Compress and upload first, then send it like a text: a failed send keeps the attachment
  const sendFile = useCallback(
    async (file, limits, caption = "") => {
      const clientId = newClientId();
      const draftConversationId = conversationRef.current;
      const placeholder = {
        id: localId(clientId),
        client_id: clientId,
        type: kindOf(file),
        text: caption,
        attachment: { filename: file.name, size: file.size },
        sender: { type: "agent" },
        ticket_id: lastTicketId,
        created_at: new Date().toISOString(),
      };
      const show = (fields) => {
        if (conversationRef.current === draftConversationId) {
          dispatch({ type: "messages/upsert", message: { ...placeholder, ...fields } });
        }
      };
      const onProgress = (stage, percent) => show({ local: "uploading", progress: { stage, percent } });

      onProgress("compress");

      try {
        const prepared = await prepareLivechatUpload(file, limits, onProgress);
        const { attachmentId, kind } = await uploadLivechatMedia(token, draftConversationId, prepared, onProgress);

        draftsRef.current.set(clientId, { conversationId: draftConversationId, text: caption, attachmentId, type: kind });
        show({ local: "sending", type: kind });
        deliver(clientId);
      } catch (error) {
        show({ local: "uploadFailed", error: error.message });
      }
    },
    [token, dispatch, deliver, lastTicketId],
  );

  const resend = useCallback(
    (clientId) => {
      if (!draftsRef.current.has(clientId)) return;

      dispatch({ type: "messages/localStatus", id: localId(clientId), local: "sending" });
      deliver(clientId);
    },
    [dispatch, deliver],
  );

  const reopenComposer = useCallback(() => setIsTicketClosed(false), []);

  return { send, sendFile, resend, isTicketClosed, reopenComposer };
};
